import React from 'react';

import { Text } from '@atlaskit/primitives/compiled/text';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import {
	getStatusCode,
	isStatusCodeError,
} from '@confluence/error-boundary/entry-points/isStatusCodeError.ts';
import { fg } from '@confluence/feature-gating';

import { i18n } from './movePublishedPageErrorMessages.ts';

const isConflictingPageNameError = (errorMessage) => {
	return (
		(errorMessage.includes('A page with the name(s)') ||
			errorMessage.includes('A content with the name(s)')) &&
		errorMessage.includes('already exist(s) in the space')
	);
};

export const getSuggestedUniqueTitle = (title: string): string => {
	const trailingCounterPattern = /\s\((\d+)\)$/;
	const match = title.match(trailingCounterPattern);
	if (match) {
		const nextCount = parseInt(match[1], 10) + 1;
		return title.replace(trailingCounterPattern, ` (${nextCount})`);
	}
	return `${title} (1)`;
};

const convertToCommaSeparatedString = (listOfPageNames) => {
	if (listOfPageNames.length === 2) {
		return listOfPageNames.join(' and ');
	} else if (listOfPageNames.length > 2) {
		return `${listOfPageNames
			.slice(0, listOfPageNames.length - 1)
			.join(', ')}, and ${listOfPageNames[listOfPageNames.length - 1]}`;
	}
	return listOfPageNames.join(', ');
};

const getErrorMessageForConflictingPageNamesError = (
	errorMessage,
	IsInEditor?: boolean,
	onActionClick?,
	isLivePage?: boolean,
) => {
	let firstPartOfMessage = 'A page with the name(s) ';
	const middlePartOfMessage = ' already exist(s) in the space ';
	let lastPartOfMessage = '. You must rename the page title before moving it.';

	if (errorMessage.indexOf(firstPartOfMessage) === -1) {
		firstPartOfMessage = 'A content with the name(s) ';
		lastPartOfMessage = '. You must rename the content title before moving it.';
	}

	const listOfConflictingNames = errorMessage
		.substring(
			errorMessage.indexOf(firstPartOfMessage) + firstPartOfMessage.length,
			errorMessage.indexOf(middlePartOfMessage),
		)
		.split(',');

	const destinationSpace = errorMessage.substring(
		errorMessage.indexOf(middlePartOfMessage) + middlePartOfMessage.length,
		errorMessage.indexOf(lastPartOfMessage),
	);
	if (listOfConflictingNames.length === 1) {
		const conflictingPageName = listOfConflictingNames[0];

		if (!IsInEditor) {
			return {
				title: i18n.singleConflictingTitleRenameErrorTitle,
				description:
					firstPartOfMessage === 'A page with the name(s) '
						? i18n.singleConflictingTitleRenamePageErrorDescription
						: i18n.singleConflictingTitleRenameContentErrorDescription,
				props: {
					values: {
						conflictingPageName,
						destinationSpace,
						strong: (chunks) => <Text as="strong">{chunks}</Text>,
					},
				},
				isConflictingTitleError: true,
				renameTitleValue: getSuggestedUniqueTitle(conflictingPageName),
			};
		}

		const title =
			isLivePage && fg('confluence_live_pages_open_beta_trait_opted_in')
				? i18n.singleConflictingLivePageNameWithinEditorErrorTitle
				: i18n.singleConflictingPageNameWithinEditorErrorTitle;

		let description;
		if (isLivePage && fg('confluence_live_pages_open_beta_trait_opted_in')) {
			description = i18n.singleConflictingLiveDocNameWithinEditorErrorDescription;
		} else if (isLivePage) {
			description = i18n.singleConflictingLivePageNameWithinEditorErrorDescription;
		} else {
			description = i18n.singleConflictingPageNameWithinEditorErrorDescription;
		}

		return {
			title,
			description,
			props: {
				values: {
					conflictingPageName,
					destinationSpace,
					strong: (chunks) => <Text as="strong">{chunks}</Text>,
				},
			},
			actions: [
				{
					key: 'move-page-error-error-cta',
					text:
						isLivePage && fg('confluence_live_pages_open_beta_trait_opted_in')
							? i18n.singleConflictingLivePageNameWithinEditorErrorCallToAction
							: i18n.singleConflictingPageNameWithinEditorErrorCallToAction,
					onClick: onActionClick,
				},
			],
		};
	} else {
		return {
			title: i18n.multipleConflictingContentNamesErrorTitle,
			description: i18n.multipleConflictingContentNamesErrorDescription,
			props: {
				values: {
					listOfConflictingNames: convertToCommaSeparatedString(listOfConflictingNames),
					destinationSpace,
				},
			},
		};
	}
};

const isRestrictionsPermissionError = (errorMessage) => {
	return (
		errorMessage.includes('You can not restrict pages in the destination space.') ||
		errorMessage.includes('You can not restrict contents in the destination space.')
	);
};

const cannotMoveDraftError = (errorMessage) => {
	return errorMessage.includes('You do not have permission to move this draft to another space.');
};

const cannotMoveDraftWithChildrenError = (errorMessage) => {
	return errorMessage.includes(
		'You cannot move a draft with children to another space without space admin permission.',
	);
};

const isNotSpaceAdminForCrossSpaceMoveError = (errorMessage) => {
	return errorMessage.includes('Only space admins can move content to another space.');
};

const lacksManageContentOperationError = (errorMessage) => {
	return errorMessage.includes(
		'Only users who can manage content can move content to another space.',
	);
};

// isTargetSameasSource and isParentChildLoop are the same error, but the api either returns 400/500.
const isParentChildLoopError = (errorMessage) => {
	return errorMessage.includes('creates a parent-child loop');
};

const isTargetSameAsSourceError = (errorMessage) => {
	return errorMessage.includes('Can not set page as its own parent.');
};

const isDestinationNonexistantError = (errorMessage) => {
	return (
		errorMessage.includes('The target page provided does not exist.') ||
		errorMessage.includes('The target content provided does not exist.') ||
		((errorMessage.includes('The target page with ID = ') ||
			errorMessage.includes('The target content with ID = ')) &&
			errorMessage.includes('is archived'))
	);
};

const isSourceNonexistantError = (errorMessage) => {
	return (
		errorMessage.includes('The source page provided does not exist.') ||
		errorMessage.includes('The source content provided does not exist.') ||
		((errorMessage.includes('The source page with ID = ') ||
			errorMessage.includes('The source content with ID =')) &&
			errorMessage.includes('is archived'))
	);
};

const isCurrentlyUpdatedError = (errorMessage) =>
	errorMessage.includes('UnexpectedRollbackException');

const isMovingTooManyPagesError = (errorMessage) =>
	errorMessage.includes("We can't support moving this many pages at this time");

const isMovingMoreThan100PagesError = (errorMessage) =>
	errorMessage.includes("We can't support moving more than 100 pages at this time");

const isAttemptingToMoveContentToSelf = (errorMessage) =>
	errorMessage.includes('Invalid to perform any move operation where source is same as target');

const isPartiallyMovedError = (errorMessage) =>
	errorMessage.includes("Can't add a parent from another space");

const getI18nMessageForKnown400Error = (
	errorMessage,
	IsInEditor?: boolean,
	onActionClick?,
	isLivePage?: boolean,
) => {
	if (isConflictingPageNameError(errorMessage)) {
		return getErrorMessageForConflictingPageNamesError(
			errorMessage,
			IsInEditor,
			onActionClick,
			isLivePage,
		);
	} else if (isAttemptingToMoveContentToSelf(errorMessage)) {
		return {
			title: i18n.movingContentToSelfErrorTitle,
			description: i18n.movingContentToSelfErrorDescription,
		};
	} else if (isParentChildLoopError(errorMessage)) {
		return {
			title: i18n.parentChildLoopErrorTitle,
			description: i18n.parentChildLoopErrorDescription,
		};
	} else if (isRestrictionsPermissionError(errorMessage)) {
		return {
			title: i18n.restrictionsPermissionErrorTitle,
			description: i18n.restrictionsPermissionErrorDescription,
		};
	} else if (isMovingMoreThan100PagesError(errorMessage)) {
		return {
			title: i18n.moreThan100PagesErrorTitle,
			description: i18n.moreThan100PagesErrorDescription,
		};
	} else if (isMovingTooManyPagesError(errorMessage)) {
		// This was previously a configurable limit, but the blanket 100-page limit (isMovingMoreThan100PagesError)
		// has been added behind a feature gate on the backend. Thus, this branch is still needed
		return {
			title: i18n.tooManyPagesErrorTitle,
			description: i18n.tooManyPagesErrorDescription,
		};
	}
	return undefined;
};

const getI18nMessageForKnown500Error = (
	errorMessage,
	IsInEditor?: boolean,
	onActionClick?,
	isLivePage?: boolean,
) => {
	if (isConflictingPageNameError(errorMessage)) {
		return getErrorMessageForConflictingPageNamesError(
			errorMessage,
			IsInEditor,
			onActionClick,
			isLivePage,
		);
	} else if (isTargetSameAsSourceError(errorMessage)) {
		return {
			title: i18n.parentChildLoopErrorTitle,
			description: i18n.parentChildLoopErrorDescription,
		};
	} else if (isCurrentlyUpdatedError(errorMessage)) {
		return {
			title: i18n.currentlyUpdatedErrorTitle,
			description: i18n.currentlyUpdatedErrorDescription,
		};
	} else if (isPartiallyMovedError(errorMessage)) {
		return {
			title: i18n.partiallyMovedErrorTitle,
			description: i18n.partiallyMovedErrorDescription,
		};
	}
	return undefined;
};

const getI18nMessageForKnown404Error = (errorMessage) => {
	if (isDestinationNonexistantError(errorMessage)) {
		return {
			title: i18n.destinationDoesNotExistErrorTitle,
			description: i18n.destinationDoesNotExistErrorDescription,
		};
	} else if (isSourceNonexistantError(errorMessage)) {
		return {
			title: i18n.sourceDoesNotExistErrorTitle,
			description: i18n.sourceDoesNotExistErrorDescription,
		};
	}
	return undefined;
};

const getI18nMessageForKnown403Error = (errorMessage) => {
	if (cannotMoveDraftError(errorMessage)) {
		return {
			title: i18n.draftPermissionsErrorTitle,
			description: i18n.draftPermissionsErrorDescription,
		};
	} else if (cannotMoveDraftWithChildrenError(errorMessage)) {
		return {
			title: i18n.draftWithChildrenErrorTitle,
			description: i18n.draftWithChildrenErrorDescription,
		};
	} else if (
		isNotSpaceAdminForCrossSpaceMoveError(errorMessage) ||
		lacksManageContentOperationError(errorMessage)
	) {
		return {
			title: i18n.notSpaceAdminForCrossSpaceMoveTitle,
			description: i18n.notSpaceAdminForCrossSpaceMoveDescription,
		};
	} else {
		return {
			title: i18n.permissionsErrorTitle,
			description: i18n.permissionsErrorDescription,
		};
	}
};

export const getI18nMessageForKnownError = (
	error: ApolloErrorInterop,
	{
		IsInEditor = false,
		onActionClick = (): void => {},
		isLivePage = false,
	}: {
		IsInEditor?: boolean | undefined;
		onActionClick?: (() => void) | undefined;
		isLivePage?: boolean | undefined;
	} = {},
):
	| {
			actions?:
				| {
						key: string;
						text: {
							id: string;
							defaultMessage: string;
							description: string;
						};
						onClick: any;
				  }[]
				| undefined;
			title: {
				id: string;
				defaultMessage: string;
				description: string;
			};
			description: any;
			props: {
				values: {
					conflictingPageName: any;
					destinationSpace: any;
					strong: (chunks: any) => React.JSX.Element;
					listOfConflictingNames?: undefined;
				};
			};
			isConflictingTitleError?: boolean;
			renameTitleValue?: string;
	  }
	| {
			title: {
				id: string;
				defaultMessage: string;
				description: string;
			};
			description: {
				id: string;
				defaultMessage: string;
				description: string;
			};
	  }
	| undefined => {
	const graphQLError = error?.graphQLErrors?.[0];
	const errorStatus = getStatusCode(graphQLError);
	const errorMessage = graphQLError?.message;
	if (errorStatus === '400') {
		return getI18nMessageForKnown400Error(errorMessage, IsInEditor, onActionClick, isLivePage);
	} else if (errorStatus === '403') {
		return getI18nMessageForKnown403Error(errorMessage);
	} else if (errorStatus === '404') {
		return getI18nMessageForKnown404Error(errorMessage);
	} else if (errorStatus === '500') {
		return getI18nMessageForKnown500Error(errorMessage, IsInEditor, onActionClick, isLivePage);
	}
	return undefined;
};

export const getI18nMessageForUnknownError = (
	error: ApolloErrorInterop,
	childPagesCount: number,
): {
	title: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	description: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} => {
	if (isStatusCodeError(error, '503') && childPagesCount >= 500) {
		return {
			title: i18n.tooManyPagesErrorTitle,
			description: i18n.tooManyPagesErrorDescription,
		};
	}

	return {
		title: i18n.defaultErrorTitle,
		description: i18n.defaultErrorDescription,
	};
};

export const getI18nMessageForNoCreatePermissions = (): {
	title: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	description: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} => ({
	title: i18n.noCreatePermissionsErrorTitle,
	description: i18n.noCreatePermissionsErrorDescription,
});
